import { useCallback, useEffect, useId, useRef, useState } from 'react'
import { useLocation, useMatch, useNavigate } from 'react-router-dom'
import { leerClienteRecordado } from '../clienteActivo'
import { useTrampaDeFoco } from '../ganchos/useTrampaDeFoco'
import { useAyuda } from './ContextoDeAyuda'
import {
  CLAVE_DEL_RECORRIDO,
  TEMAS_DEL_RECORRIDO,
  TEXTOS_DEL_RECORRIDO,
  VERSION_DEL_RECORRIDO,
  pasosDelRecorrido,
  rutaDelPaso,
  type PasoDelRecorrido,
  type TemaDelRecorrido,
} from './contenidoDelRecorrido'
import './recorrido.css'

/** Cuánto se espera a que aparezca el elemento de un paso antes de saltarlo. */
const TIEMPO_LIMITE_MS = 8000
const SONDEO_MS = 100
/** Debajo de este ancho la tarjeta se ancla abajo, a todo lo ancho (ver recorrido.css). */
const ANCHO_DE_TELEFONO = 560
const ANCHO_DE_TARJETA = 360
const ALTO_ESTIMADO_DE_TARJETA = 230
const MARGEN = 16
/** Holgura del resaltado alrededor del elemento. */
const HOLGURA = 6

interface Corrida {
  pasos: PasoDelRecorrido[]
  clienteId: string
}

/**
 * El cliente sobre el que corren los pasos de un cliente: el de la ruta, el recordado o, si no hay ninguno,
 * el que muestra el selector de la barra lateral (que cae en el primero de la cartera sin recordarlo).
 */
function clienteParaElRecorrido(idEnLaRuta: string | undefined): string {
  if (idEnLaRuta) return idEnLaRuta
  const recordado = leerClienteRecordado()
  if (recordado) return recordado
  const selector = document.querySelector<HTMLSelectElement>('select[data-recorrido="selector-de-cliente"]')
  return selector?.value ?? ''
}

function buscarDestino(destino: string): HTMLElement | null {
  const elemento = document.querySelector<HTMLElement>(`[data-recorrido="${destino}"]`)
  // Presente pero oculto (la barra lateral en un teléfono, por ejemplo) cuenta como ausente.
  if (!elemento || (typeof elemento.checkVisibility === 'function' && !elemento.checkVisibility())) return null
  return elemento
}

const pideMenosMovimiento = () =>
  typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches

/**
 * El recorrido guiado de lo básico (RF-1701, RF-1702, RF-1708). Arranca solo en el primer ingreso —cuando hay
 * estado y el usuario no ha visto la versión vigente— o cuando lo piden desde «Ayuda», completo o por tema.
 * Al terminar u omitir se registra una sola vez (no paso por paso: el límite de peticiones es del despacho).
 */
export function Recorrido({ tiempoLimiteMs = TIEMPO_LIMITE_MS }: { tiempoLimiteMs?: number }) {
  const { estado, rol, vioRecorrido, marcarRecorrido, recorridoSolicitado, cerrarRecorridoSolicitado } = useAyuda()
  const enCliente = useMatch('/clientes/:id/*')
  const idEnLaRuta = enCliente?.params.id
  const [corrida, setCorrida] = useState<Corrida | null>(null)
  const yaSeRevisoElPrimerIngreso = useRef(false)

  const arrancar = useCallback((tema: TemaDelRecorrido | null) => {
    const clienteId = clienteParaElRecorrido(idEnLaRuta)
    setCorrida({ pasos: pasosDelRecorrido(tema, rol, clienteId !== ''), clienteId })
  }, [idEnLaRuta, rol])

  // RF-1702 · Solo la primera vez: se revisa una vez por sesión, en cuanto llega lo que ya vio.
  useEffect(() => {
    if (!estado || yaSeRevisoElPrimerIngreso.current) return
    yaSeRevisoElPrimerIngreso.current = true
    if (!vioRecorrido(CLAVE_DEL_RECORRIDO, VERSION_DEL_RECORRIDO)) arrancar(null)
  }, [estado, vioRecorrido, arrancar])

  // Desde «Ayuda»: cada petición es un objeto nuevo, así que pedir dos veces el mismo tema vuelve a arrancar.
  useEffect(() => {
    if (recorridoSolicitado) arrancar(recorridoSolicitado.tema as TemaDelRecorrido | null)
    // Sin `arrancar` en las dependencias, a propósito: cambia con la ruta, y si estuviera, cada pantalla que
    // abre el propio recorrido lo volvería a arrancar desde el primer paso.
  }, [recorridoSolicitado])

  const terminar = useCallback(() => {
    if (!vioRecorrido(CLAVE_DEL_RECORRIDO, VERSION_DEL_RECORRIDO)) marcarRecorrido(CLAVE_DEL_RECORRIDO, VERSION_DEL_RECORRIDO)
    cerrarRecorridoSolicitado()
    setCorrida(null)
  }, [vioRecorrido, marcarRecorrido, cerrarRecorridoSolicitado])

  if (!corrida || corrida.pasos.length === 0) return null
  return <TarjetaDelRecorrido corrida={corrida} tiempoLimiteMs={tiempoLimiteMs} alTerminar={terminar} />
}

function TarjetaDelRecorrido({
  corrida,
  tiempoLimiteMs,
  alTerminar,
}: {
  corrida: Corrida
  tiempoLimiteMs: number
  alTerminar: () => void
}) {
  const { pasos, clienteId } = corrida
  const [indice, setIndice] = useState(0)
  const direccion = useRef<1 | -1>(1)
  const [elemento, setElemento] = useState<HTMLElement | null>(null)
  const [rect, setRect] = useState<DOMRect | null>(null)
  const [ancho, setAncho] = useState(() => window.innerWidth)
  const navegar = useNavigate()
  const ubicacion = useLocation()
  const rutaActual = useRef(ubicacion.pathname)
  rutaActual.current = ubicacion.pathname
  const panelRef = useTrampaDeFoco<HTMLDivElement>(true, alTerminar)
  const idTitulo = useId()
  const idTexto = useId()

  const paso = pasos[indice]
  const esUltimo = indice === pasos.length - 1

  const ir = useCallback((destino: number, sentido: 1 | -1) => {
    direccion.current = sentido
    if (destino >= pasos.length) alTerminar()
    else setIndice(Math.max(0, destino))
  }, [pasos.length, alTerminar])

  // Cada paso: abre su pantalla si hace falta y espera a su elemento. Si no aparece a tiempo, se salta en la
  // misma dirección en que se venía; hacia atrás, el primero que no aparece deja pasar al siguiente.
  useEffect(() => {
    setElemento(null)
    setRect(null)
    const ruta = rutaDelPaso(paso, clienteId)
    if (ruta !== null && ruta !== rutaActual.current) navegar(ruta)
    if (paso.destino === null) return

    const destino = paso.destino
    const inicio = Date.now()
    let temporizador: ReturnType<typeof setTimeout>
    const sondear = () => {
      const encontrado = buscarDestino(destino)
      if (encontrado) {
        encontrado.scrollIntoView?.({ block: 'center', behavior: pideMenosMovimiento() ? 'auto' : 'smooth' })
        setElemento(encontrado)
        setRect(encontrado.getBoundingClientRect())
      } else if (Date.now() - inicio >= tiempoLimiteMs) {
        const sentido = indice === 0 ? 1 : direccion.current
        ir(indice + sentido, sentido)
      } else {
        temporizador = setTimeout(sondear, SONDEO_MS)
      }
    }
    sondear()
    return () => clearTimeout(temporizador)
  }, [indice, paso, clienteId, navegar, tiempoLimiteMs, ir])

  // El resaltado sigue al elemento si la página se desplaza o cambia de tamaño.
  useEffect(() => {
    const medir = () => {
      setAncho(window.innerWidth)
      if (elemento) setRect(elemento.getBoundingClientRect())
    }
    window.addEventListener('resize', medir)
    window.addEventListener('scroll', medir, true)
    return () => {
      window.removeEventListener('resize', medir)
      window.removeEventListener('scroll', medir, true)
    }
  }, [elemento])

  // Flechas del teclado: avanzar y retroceder. Esc lo atiende la trampa de foco (omitir).
  useEffect(() => {
    const alTeclado = (evento: KeyboardEvent) => {
      if (evento.key === 'ArrowRight') { evento.preventDefault(); ir(indice + 1, 1) }
      else if (evento.key === 'ArrowLeft' && indice > 0) { evento.preventDefault(); ir(indice - 1, -1) }
    }
    document.addEventListener('keydown', alTeclado)
    return () => document.removeEventListener('keydown', alTeclado)
  }, [indice, ir])

  const tema = TEMAS_DEL_RECORRIDO.find((t) => t.clave === paso.tema)
  const progreso = TEXTOS_DEL_RECORRIDO.progreso(indice + 1, pasos.length)
  const conResaltado = rect !== null

  return (
    <div className="recorrido-capa">
      {conResaltado
        ? (
          <div
            className="recorrido-resaltado"
            aria-hidden="true"
            style={{
              top: rect.top - HOLGURA,
              left: rect.left - HOLGURA,
              width: rect.width + HOLGURA * 2,
              height: rect.height + HOLGURA * 2,
            }}
          />
        )
        : <div className="recorrido-velo" aria-hidden="true" />}

      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={idTitulo}
        aria-describedby={idTexto}
        className={`recorrido-tarjeta${conResaltado ? '' : ' centrada'}`}
        style={conResaltado ? posicionDeLaTarjeta(rect, ancho) : undefined}
      >
        <p className="recorrido-solo-lectores" role="status" aria-live="polite">
          {`${progreso}: ${paso.titulo}`}
        </p>
        <div className="recorrido-contenido" key={paso.id}>
          <p className="recorrido-progreso">
            {progreso}{tema ? ` · ${tema.titulo}` : ''}
          </p>
          <h2 id={idTitulo}>{paso.titulo}</h2>
          <p id={idTexto}>{paso.texto}</p>
        </div>
        <div className="recorrido-acciones">
          <button type="button" onClick={() => ir(indice - 1, -1)} disabled={indice === 0}>
            {TEXTOS_DEL_RECORRIDO.anterior}
          </button>
          <button type="button" className="primario" onClick={() => ir(indice + 1, 1)}>
            {esUltimo ? TEXTOS_DEL_RECORRIDO.terminar : TEXTOS_DEL_RECORRIDO.siguiente}
          </button>
          <button type="button" className="enlace recorrido-omitir" onClick={alTerminar}>
            {TEXTOS_DEL_RECORRIDO.omitir}
          </button>
        </div>
      </div>
    </div>
  )
}

/**
 * Debajo del elemento si cabe; si no, arriba. Alineada a su izquierda sin salirse de la pantalla. En un
 * teléfono la hoja de estilos la ancla abajo a todo lo ancho, sin importar esto.
 */
export function posicionDeLaTarjeta(rect: DOMRect, anchoDeVentana: number): { top: number; left: number } {
  const altoDeVentana = window.innerHeight
  const abajo = rect.bottom + HOLGURA + MARGEN
  const top = abajo + ALTO_ESTIMADO_DE_TARJETA <= altoDeVentana
    ? abajo
    : Math.max(MARGEN, rect.top - HOLGURA - MARGEN - ALTO_ESTIMADO_DE_TARJETA)
  const maximo = Math.max(MARGEN, anchoDeVentana - ANCHO_DE_TARJETA - MARGEN)
  const left = anchoDeVentana < ANCHO_DE_TELEFONO ? MARGEN : Math.min(Math.max(MARGEN, rect.left), maximo)
  return { top, left }
}
